// Built-in functions and events — the SINGLE source of truth for the
// builtin surface. The type-checker consults this table when resolving
// call expressions, before falling back to user-declared functions;
// the codegen DERIVES its wasm import table from it (emit-context.ts
// lowers each entry: one i32 per parameter — callback slots become a
// cbId i32 — and void → no result); the runtime scaffold DERIVES its
// stub-return table from each entry's returnType (base-env.ts); and
// the callback-dispatcher map is derived from the `dispatcher` names
// on the contracts below. Adding a builtin is ONE entry here plus the
// real host implementation — there is no parallel table to forget.
//
// "role" filters tell the checker which on-block (client / server) the
// builtin is permitted in. `undefined` = allowed in either role.
import { T_I8, T_I16, T_I32, T_U8, T_U16, T_U32, T_BOOL, T_STRING, T_IMAGE, T_SOUND, T_CLIENT, T_VOID, } from "./types.js";
const u8Arr = { kind: "array", element: T_U8 };
const i32Arr = { kind: "array", element: T_I32 };
const u32Arr = { kind: "array", element: T_U32 };
const stringArr = { kind: "array", element: T_STRING };
// Capacity-typed returns for builtins whose output is a FIXED-width string
// (docs/memory-model.md §"Strings", slice 5). Bounding the return type lets the
// result land directly in a `string<N>` slot (which recycles its escaped block
// by size class) instead of forcing an unbounded string. Only genuinely
// fixed-width outputs get a capacity — variable-length ones (URLs, labels,
// config, substrings) stay unbounded rather than invent a bound.
const string32 = { kind: "string", capacity: 32 }; // uuid() — dash-stripped UUIDv4 hex
const string40 = { kind: "string", capacity: 40 }; // local_rom_hash() — SHA-1 hex
// Callback contracts for the persistence builtins. See docs/persistence.md
// §Callback signatures for the full surface.
const DB_GET_CONTRACT = {
    params: [
        { name: "success", type: T_BOOL },
        { name: "data", type: u8Arr },
        { name: "ctx", type: T_I32 },
    ],
    dispatcher: "__cb_db_get",
};
// Fetch's callback shape is identical to db_get's (success, bytes, ctx).
// Kept as a separate name for documentation and to leave room for either
// contract to evolve independently later.
const FETCH_CONTRACT = {
    params: [
        { name: "success", type: T_BOOL },
        { name: "data", type: u8Arr },
        { name: "ctx", type: T_I32 },
    ],
    dispatcher: "__cb_fetch",
};
const DB_LIST_CONTRACT = {
    params: [
        { name: "success", type: T_BOOL },
        { name: "keys", type: stringArr },
        { name: "ctx", type: T_I32 },
    ],
    dispatcher: "__cb_db_list",
};
const DB_WRITE_CONTRACT = {
    // Shared by db_set and db_del — which is also what makes the two
    // builtins share the __cb_db_write dispatcher (their callback
    // signatures are identical: success, ctx).
    params: [
        { name: "success", type: T_BOOL },
        { name: "ctx", type: T_I32 },
    ],
    dispatcher: "__cb_db_write",
};
// Execution-hook callback — see docs/execution-hooks-and-bank-switching.md.
// The only argument is the CPU address (pc) the trap fired at; everything
// else the callback needs it reads live via read_u8 / read_reg.
const ON_EXEC_CONTRACT = {
    params: [
        { name: "pc", type: T_U32 },
    ],
    dispatcher: "__cb_on_exec",
};
// ----- builtin functions -----
export const BUILTIN_FUNCTIONS = Object.freeze([
    // I/O — `print` has no import of its own; the codegen lowers each
    // call to repeated print_i32/print_str calls plus a print_end.
    { name: "print", params: [], returnType: T_VOID, variadic: true, importName: null },
    // persistence — see docs/persistence.md. Available on both client and
    // server; the runtime routes operations to the appropriate backing
    // store. All four are async; the callback parameter is a callback slot
    // (the parser accepts an inline function literal or a top-level
    // function name here). `db_set` and `db_del` make the callback +
    // trailing ctx jointly optional (fire-and-forget mode).
    {
        name: "db_get",
        params: [
            { name: "namespace", type: T_STRING },
            { name: "key", type: T_STRING },
            { name: "cb", type: T_VOID, callback: DB_GET_CONTRACT },
            { name: "ctx", type: T_I32, default: { kind: "int", value: 0n } },
        ],
        returnType: T_VOID,
    },
    {
        name: "db_set",
        params: [
            { name: "namespace", type: T_STRING },
            { name: "key", type: T_STRING },
            { name: "data", type: u8Arr },
            { name: "cb", type: T_VOID, callback: DB_WRITE_CONTRACT, optional: true },
            { name: "ctx", type: T_I32, default: { kind: "int", value: 0n }, optional: true },
        ],
        returnType: T_VOID,
    },
    {
        name: "db_del",
        params: [
            { name: "namespace", type: T_STRING },
            { name: "key", type: T_STRING },
            { name: "cb", type: T_VOID, callback: DB_WRITE_CONTRACT, optional: true },
            { name: "ctx", type: T_I32, default: { kind: "int", value: 0n }, optional: true },
        ],
        returnType: T_VOID,
    },
    {
        // The spec gives `prefix` a default of "" but the existing default
        // mechanism only carries int/bool literals. Callers can pass an
        // empty string explicitly when they don't want a prefix filter.
        //
        // Ordered pagination — `start` / `limit` / `reverse` — lets a script
        // read an ordered PAGE of keys instead of the whole matching set at
        // once (docs/persistence.md §"Ordered pagination"). They append AFTER
        // the historical `cb` + `ctx` tail deliberately: builtin arguments bind
        // positionally (the checker's resolveArgs), so `cb` must stay at index
        // 2 and `ctx` at index 3 for the existing `db_list(ns, prefix, cb)` and
        // `db_list(ns, prefix, cb, ctx)` call sites to keep compiling and
        // behaving identically. New callers reach the paging knobs positionally
        // (`db_list(ns, pfx, cb, 0, "cursor", 20, true)`) or by named argument
        // (`db_list(ns, pfx, cb, limit: 20, reverse: true)`). The callback
        // contract (success, keys, ctx) is unchanged.
        //
        //   - start:   cursor. EXCLUSIVE, so "next page" is a clean pass-through
        //              of the last key received. "" begins at the first key
        //              (ascending) / the last key (reverse). Cannot carry a
        //              string default through the int/bool-only default
        //              mechanism, so it is `optional` and the host binding maps
        //              an omitted arg (null pointer) to "".
        //   - limit:   max keys returned; 0 = unlimited (today's behaviour).
        //   - reverse: false = ascending (K > start), true = descending (K < start).
        name: "db_list",
        params: [
            { name: "namespace", type: T_STRING },
            { name: "prefix", type: T_STRING },
            { name: "cb", type: T_VOID, callback: DB_LIST_CONTRACT },
            { name: "ctx", type: T_I32, default: { kind: "int", value: 0n } },
            { name: "start", type: T_STRING, optional: true },
            { name: "limit", type: T_I32, default: { kind: "int", value: 0n } },
            { name: "reverse", type: T_BOOL, default: { kind: "bool", value: false } },
        ],
        returnType: T_VOID,
    },
    // fetch — see docs/fetch.md. Client-only; the server import is bound
    // to a stub that always reports success=false. The runtime validates
    // the URL (HTTPS, with http allowed only for localhost), consults its
    // in-memory cache, applies concurrency + rate caps, then invokes the
    // callback with the response body. Failures fire the callback with
    // success=false and (if declared) `event fetch_error`.
    {
        name: "fetch", role: "client",
        params: [
            { name: "url", type: T_STRING },
            { name: "cb", type: T_VOID, callback: FETCH_CONTRACT },
            { name: "ctx", type: T_I32, default: { kind: "int", value: 0n } },
        ],
        returnType: T_VOID,
    },
    // memory
    { name: "read_u8", params: [{ name: "addr", type: T_U32 }], returnType: T_U8 },
    { name: "read_u16", params: [{ name: "addr", type: T_U32 }], returnType: T_U16 },
    { name: "read_u32", params: [{ name: "addr", type: T_U32 }], returnType: T_U32 },
    { name: "read_i8", params: [{ name: "addr", type: T_U32 }], returnType: T_I8 },
    { name: "read_i16", params: [{ name: "addr", type: T_U32 }], returnType: T_I16 },
    { name: "read_i32", params: [{ name: "addr", type: T_U32 }], returnType: T_I32 },
    { name: "write_u8", params: [{ name: "addr", type: T_U32 }, { name: "value", type: T_U8 }], returnType: T_VOID },
    { name: "write_u16", params: [{ name: "addr", type: T_U32 }, { name: "value", type: T_U16 }], returnType: T_VOID },
    { name: "write_u32", params: [{ name: "addr", type: T_U32 }, { name: "value", type: T_U32 }], returnType: T_VOID },
    { name: "write_i8", params: [{ name: "addr", type: T_U32 }, { name: "value", type: T_I8 }], returnType: T_VOID },
    { name: "write_i16", params: [{ name: "addr", type: T_U32 }, { name: "value", type: T_I16 }], returnType: T_VOID },
    { name: "write_i32", params: [{ name: "addr", type: T_U32 }, { name: "value", type: T_I32 }], returnType: T_VOID },
    // execution hooks — see docs/execution-hooks-and-bank-switching.md.
    //
    // on_exec(target, cb, priority=0) attaches a callback to "this physical ROM
    // location executed." The callback fires pre-step (before the trapped
    // instruction runs), so a write to PC/registers from inside it supersedes
    // that instruction — which is what makes redirection work (§4). The target is
    // a physical coordinate built with prg()/bank()/cpu(), never a raw CPU
    // address (which is bank-ambiguous, §1). clear_exec removes a hook by the
    // handle on_exec returned. All client-only: hooks act on the emulated CPU.
    //
    // `priority` orders multiple hooks on the SAME location: they fire in
    // ASCENDING priority, so a higher number runs LATER (and thus wins when two
    // hooks write the same state). Hooks with equal priority fire in
    // registration order. This lets independently-authored members compose on
    // one seam without knowing each other — a "base" layer that constructs state
    // and an "override" layer that corrects it agree only on a numeric
    // convention (see docs §5.1), never on each other's identity. Default 0.
    {
        name: "on_exec", role: "client",
        params: [
            { name: "target", type: T_U32 },
            { name: "cb", type: T_VOID, callback: ON_EXEC_CONTRACT },
            { name: "priority", type: T_I32, default: { kind: "int", value: 0n } },
        ],
        returnType: T_U32,
    },
    { name: "clear_exec", params: [{ name: "handle", type: T_U32 }], returnType: T_VOID, role: "client" },
    // Coordinate constructors — encode a region-tagged physical location into a
    // single u32 for on_exec / write_rom. prg() is the primary form (a flat
    // PRG-ROM offset); bank(n, addr) is sugar for a 16KB-window bank+address;
    // cpu(addr) is the escape hatch for a raw CPU address (fixed banks, vectors).
    { name: "prg", params: [{ name: "offset", type: T_U32 }], returnType: T_U32, role: "client" },
    { name: "bank", params: [{ name: "n", type: T_U32 }, { name: "addr", type: T_U32 }], returnType: T_U32, role: "client" },
    { name: "cpu", params: [{ name: "addr", type: T_U32 }], returnType: T_U32, role: "client" },
    // CPU register access — reg is one of the injected REG_* constants
    // (REG_A/X/Y/SP/P/PC). PC is 16-bit, the rest 8-bit. write_reg(REG_PC, …)
    // from inside an on_exec callback redirects execution (pre-step, §4).
    { name: "read_reg", params: [{ name: "reg", type: T_U32 }], returnType: T_U32, role: "client" },
    { name: "write_reg", params: [{ name: "reg", type: T_U32 }, { name: "value", type: T_U32 }], returnType: T_VOID, role: "client" },
    // ROM-image write — patch the host's PRG-ROM image (real ROM is read-only;
    // ours isn't). coord is a prg()/bank() coordinate, byte is the new value.
    // This is how you NOP a branch or swap an opcode (§8.2).
    { name: "write_rom", params: [{ name: "coord", type: T_U32 }, { name: "byte", type: T_U32 }], returnType: T_VOID, role: "client" },
    // PPU / video memory — direct read/write of the emulated PPU address space
    // ($0000-$3FFF: pattern tables, nametables, palette). A HOST-SIDE side-channel
    // that bypasses the console's $2006/$2007 register protocol entirely: no
    // address latch, no vblank window, no VRAM staging buffer. So a mod paints
    // the background directly — write_ppu(0x2000 + row*32 + col, tile) — instead
    // of fighting for the game's own tiny VRAM buffer. Nametable mirroring is
    // applied for you (same as the real data port). Emulator-only + client-only;
    // on a core without a pokeable PPU these fall to the warn-once base stub.
    { name: "read_ppu", params: [{ name: "addr", type: T_U32 }], returnType: T_U8, role: "client" },
    { name: "write_ppu", params: [{ name: "addr", type: T_U32 }, { name: "value", type: T_U8 }], returnType: T_VOID, role: "client" },
    // length
    // `len` is special-cased in the checker because it accepts any array
    // type or string. We list it here with `T_ERROR` placeholder so callers
    // know it exists; the checker doesn't use the params for it.
    // (We use a phantom signature; the resolver intercepts before checking.)
    // networking (client → its server). A client has exactly one peer, so
    // no recipient is named. `channel` is the shared-bus channel id (a u16):
    // the host stamps it on the wire and routes the frame on the far side only
    // to members that `listen` on it, so co-tenant scripts can't read each
    // other's traffic (docs/multi-script-client.md §"the wire is channelled").
    {
        name: "send",
        importName: "send_client",
        params: [
            { name: "channel", type: T_I32 },
            { name: "data", type: u8Arr },
            { name: "reliable", type: T_BOOL, default: { kind: "bool", value: true } },
        ],
        returnType: T_VOID,
        role: "client",
    },
    // networking (server → one client). A server has many peers, so the
    // recipient is required — the name carries the asymmetry. `channel` as above.
    {
        name: "send_to",
        importName: "send_server",
        params: [
            { name: "to", type: T_CLIENT },
            { name: "channel", type: T_I32 },
            { name: "data", type: u8Arr },
            { name: "reliable", type: T_BOOL, default: { kind: "bool", value: true } },
        ],
        returnType: T_VOID,
        role: "server",
    },
    // interest management (area-of-interest culling) — see
    // docs/interest-management.md. The script declares each slot's interest
    // region; the HOST owns recipient selection (it knows the regions) so the
    // server stays game-agnostic — it culls fan-out by generic geometry, never
    // by parsing game packets.
    //
    // `set_interest` records a slot's interest region for this tick:
    //   - `area` is a discrete layer/partition key — two slots in different
    //     areas never overlap regardless of x/y (in SMB, area = level_from_map,
    //     which subsumes the bonus-pipe / sub-stage case).
    //   - `x, y, w, h` is the continuous interest BOX in world coordinates. It
    //     must be the view PLUS the margin band (not the bare camera window):
    //     culling is indistinguishable from packet loss to the resilient-ghost
    //     coast, so a region wider than the screen lets a ghost's prediction
    //     converge before it scrolls into view. A slot whose interest is never
    //     set is relevant to no one (matching "not in the level yet").
    {
        name: "set_interest",
        params: [
            { name: "slot", type: T_I32 },
            { name: "area", type: T_I32 },
            { name: "x", type: T_I32 },
            { name: "y", type: T_I32 },
            { name: "w", type: T_I32 },
            { name: "h", type: T_I32 },
        ],
        returnType: T_VOID,
        role: "server",
    },
    // `relay_interested` delivers `data` to every occupied slot whose interest
    // region overlaps `subject_slot`'s region (same area AND boxes intersect) —
    // the script calls it once per subject per tick instead of looping over
    // receivers with `send_to`. The script still owns packing; the host owns the
    // O(k²) overlap pass and the leave signal: when a subject leaves a receiver's
    // interest set the host fires `event peer_left(slot)` on that receiver
    // exactly once, so the ghost is dropped cleanly instead of dead-reckoned.
    {
        name: "relay_interested",
        params: [
            { name: "subject_slot", type: T_I32 },
            { name: "channel", type: T_I32 },
            { name: "data", type: u8Arr },
            { name: "reliable", type: T_BOOL, default: { kind: "bool", value: true } },
        ],
        returnType: T_VOID,
        role: "server",
    },
    // networking (node → its parent) — the server-side "up" verbs
    // (docs/node-federation.md). A node has exactly one parent, so like
    // `send` (client → its one server) no recipient is named — the symmetry
    // is the point: a server is a client to the tier above it.
    //
    // `connect_up(url, handoff)` opens this instance's parent link: an
    // OUTBOUND connection to the instance at `url`, carrying `handoff` as the
    // connect handshake exactly as a client's connection does. From the
    // parent's side the child is an ordinary connecting client — it lands in
    // the slot table and fires `event connect` — so the parent → children
    // direction needs no new surface. Only speaking upward is new.
    {
        name: "connect_up",
        params: [
            { name: "url", type: T_STRING },
            { name: "handoff", type: u8Arr },
        ],
        returnType: T_VOID,
        role: "server",
    },
    // `send_up(channel, data, reliable)` sends to the parent over the link
    // opened by `connect_up`. Same shape as the client's `send`; the parent
    // receives it through its ordinary `event receive(from, data)`, and
    // replies with `send_to(from, …)` which arrives back here as
    // `event receive_from_above(data)`.
    {
        name: "send_up",
        importName: "send_up",
        params: [
            { name: "channel", type: T_I32 },
            { name: "data", type: u8Arr },
            { name: "reliable", type: T_BOOL, default: { kind: "bool", value: true } },
        ],
        returnType: T_VOID,
        role: "server",
    },
    // data packing
    { name: "pack", params: [{ name: "values", type: i32Arr }, { name: "widths", type: i32Arr }], returnType: u8Arr },
    { name: "unpack", params: [{ name: "bytes", type: u8Arr }, { name: "widths", type: i32Arr }], returnType: i32Arr },
    // Compression — pure, synchronous u8[] -> u8[] transforms (raw DEFLATE via a
    // single bundled impl, so output is byte-identical across every runtime tier;
    // see docs/compression.md). Capability-neutral, like pack/unpack: no network,
    // storage, or clock. decompress returns [] on a malformed stream rather than
    // trapping. Available on both client and server (no `role`).
    { name: "compress", params: [{ name: "data", type: u8Arr }], returnType: u8Arr },
    { name: "decompress", params: [{ name: "data", type: u8Arr }], returnType: u8Arr },
    // Decode a base64-encoded string to its raw byte sequence. Standard
    // base64 alphabet, padding optional. Useful for inlining binary
    // assets (PNGs, palette tables, ROM patches) inside a .munos source
    // — see docs/script-distribution.md "Single-file scripts".
    { name: "base64_decode", params: [{ name: "s", type: T_STRING }], returnType: u8Arr },
    // images
    {
        name: "image_literal",
        params: [
            { name: "w", type: T_I32 },
            { name: "h", type: T_I32 },
            { name: "data", type: T_STRING },
        ],
        returnType: T_IMAGE,
    },
    { name: "image_from_bytes", params: [{ name: "data", type: u8Arr }], returnType: T_IMAGE },
    {
        // Decode a PNG byte blob and take an (x, y, w, h) sub-region as a
        // palette-indexed image. Auto-palette: alpha < 128 → index 0,
        // opaque colors sorted by luminance. All calls with the same `data`
        // reference share the same palette (see palette_from_png_bytes).
        // Pair with `base64_decode` to inline a PNG inside the .munos
        // source — this is the only image-loading path; scripts ship their
        // assets as base64 literals.
        name: "image_from_png_bytes",
        params: [
            { name: "data", type: u8Arr },
            { name: "x", type: T_I32 },
            { name: "y", type: T_I32 },
            { name: "w", type: T_I32 },
            { name: "h", type: T_I32 },
        ],
        returnType: T_IMAGE,
        role: "client",
    },
    {
        // Return the auto-generated palette for a PNG byte blob, with slot
        // 0 = transparent. Pairs with image_from_png_bytes.
        name: "palette_from_png_bytes",
        params: [{ name: "data", type: u8Arr }],
        returnType: u32Arr,
        role: "client",
    },
    {
        // Decode a custom 3-byte-header indexed bitmap (see
        // packages/munos/src/runtime/bitmap.ts for the format). Width,
        // height, and per-bitmap bit depth live in the header; the
        // bitmap bytes themselves carry no origin / offset data.
        //
        // The dx/dy args are the script author's per-pose animation tuning
        // — pixels added to drawImage's (x, y) every time this image is
        // drawn. Use them to keep a walk cycle's head stable while legs and
        // arms swing in and out (tight-cropping different frames produces
        // slightly different bbox shapes, which would otherwise make the
        // visible art "bounce" frame-to-frame). For static images that
        // don't need alignment tuning, pass 0, 0.
        //
        // Pair with `base64_decode` to inline the asset bytes in the
        // .munos source. Preferred over image_from_png_bytes for sprites:
        // 2-3x smaller for 4-color art, no PNG decoder overhead at runtime.
        name: "image_from_bitmap_bytes",
        params: [
            { name: "data", type: u8Arr },
            { name: "dx", type: T_I32 },
            { name: "dy", type: T_I32 },
        ],
        returnType: T_IMAGE,
        role: "client",
    },
    {
        // Draw an indexed image onto the framebuffer.
        //
        // `clipX` / `clipY` / `clipW` / `clipH` are SOURCE-space — they pick
        // a sub-rectangle of the image to draw. `clipW = -1` / `clipH = -1`
        // mean "use the full remaining width/height from clipX/clipY."
        //
        // `clipRectX1` / `clipRectY1` / `clipRectX2` / `clipRectY2` are
        // SCREEN-space — a scissor rect. Any pixel that would land at a
        // screen position outside [clipRectX1, clipRectX2) × [clipRectY1,
        // clipRectY2) is suppressed. Each side defaults to -1 meaning
        // "unbounded on this side" — drawImage's behavior is unchanged
        // when the script passes none of them. Use them to declare an
        // interior viewport (e.g. `clipRectY1 = 32` to keep a ghost from
        // rendering over a top-row HUD) without folding the clip math
        // into per-pose source-space arithmetic in the script. The rig's
        // viewport analyzer compares against the effective post-scissor
        // bbox: a draw whose natural footprint would cross an interior
        // viewport boundary passes when clipRect keeps the visible pixels
        // inside it.
        //
        // `behind_background` (default false) opts the draw into OAM
        // background-priority: any pixel landing where the local console's
        // background is opaque (non-backdrop) is suppressed, so the sprite
        // sinks behind level geometry the way a hardware sprite with its
        // background-priority attribute bit set does (e.g. Mario descending a
        // pipe). It reproduces true priority against the real background, so no
        // per-pose clip geometry is needed. A no-op on consoles/hosts without a
        // background-priority model (the host simply has no mask to consult).
        name: "draw_image",
        // Host import symbol stays `drawImage` — an internal ABI name, like
        // send/send_to's `send_client`/`send_server`. Only the Munos surface
        // spelling is snake_case.
        importName: "drawImage",
        params: [
            { name: "img", type: T_IMAGE },
            { name: "pal", type: u32Arr },
            { name: "x", type: T_I32 },
            { name: "y", type: T_I32 },
            { name: "flip_x", type: T_BOOL, default: { kind: "bool", value: false } },
            { name: "flip_y", type: T_BOOL, default: { kind: "bool", value: false } },
            { name: "clip_x", type: T_I32, default: { kind: "int", value: 0n } },
            { name: "clip_y", type: T_I32, default: { kind: "int", value: 0n } },
            { name: "clip_w", type: T_I32, default: { kind: "int", value: -1n } },
            { name: "clip_h", type: T_I32, default: { kind: "int", value: -1n } },
            { name: "clip_rect_x1", type: T_I32, default: { kind: "int", value: -1n } },
            { name: "clip_rect_y1", type: T_I32, default: { kind: "int", value: -1n } },
            { name: "clip_rect_x2", type: T_I32, default: { kind: "int", value: -1n } },
            { name: "clip_rect_y2", type: T_I32, default: { kind: "int", value: -1n } },
            { name: "behind_background", type: T_BOOL, default: { kind: "bool", value: false } },
        ],
        returnType: T_VOID,
        role: "client",
    },
    // sound (docs/sound.md §7) — mirrors the image/draw pair: create an
    // opaque handle from inlined asset bytes, then trigger it. The bytes are
    // a VGM register-log (authentic captured SFX/music or a tracker export);
    // playback runs them through the core's own APU, so a sound is data, not
    // a recording. Pair with base64_decode to inline a .vgm in the source.
    {
        name: "sound_from_vgm",
        params: [{ name: "data", type: u8Arr }],
        returnType: T_SOUND,
        role: "client",
    },
    {
        // Trigger a sound. `loop` distinguishes one-shot SFX (false, the
        // default) from looping music (true → repeats at the VGM's loop
        // point). Emits a SoundCall; the host tier decides how to voice it.
        name: "play_sound",
        params: [
            { name: "snd", type: T_SOUND },
            { name: "loop", type: T_BOOL, default: { kind: "bool", value: false } },
        ],
        returnType: T_VOID,
        role: "client",
    },
    // deployment / shards
    { name: "list_shards", params: [], returnType: stringArr },
    // Script library — labels of scripts the server is configured to host.
    // The first-party lobby uses this to populate its mode list, but it's
    // a generic introspection primitive — any script can call it to see
    // what siblings it could `spawn`. See docs/instances.md.
    { name: "list_scripts", params: [], returnType: stringArr, role: "server" },
    // Instance management — see docs/instances.md §"The spawn primitive".
    // `spawn` creates a new instance of a library script with the given
    // ID; returns true on success, false if the ID is already in use. The
    // `data` payload is per-instance configuration delivered to the new
    // instance's `event init` startup hook (once, before any client
    // connects) — distinct from the per-player `handoff` that rides a
    // hop/redirect to `event connect`.
    // `find_instance` returns the ID of any live instance running a given
    // script label, or empty string if none. `list_instances` returns all
    // live instance IDs (excluding the entry-point instance itself).
    {
        name: "spawn",
        params: [
            { name: "script_label", type: T_STRING },
            { name: "instance_id", type: T_STRING },
            { name: "data", type: u8Arr },
            { name: "persistent", type: T_BOOL },
        ],
        returnType: T_BOOL,
        role: "server",
    },
    { name: "find_instance", params: [{ name: "script_label", type: T_STRING }], returnType: T_STRING, role: "server" },
    { name: "list_instances", params: [], returnType: stringArr, role: "server" },
    // Instance lifecycle control — see docs/instances.md §Lifecycle. `kill`
    // forcibly tears down a target instance the caller spawned (transitively),
    // cascading to the target's whole spawn subtree so no grandchild is
    // orphaned; it returns true on success, false if the target doesn't exist
    // or the caller isn't allowed to reach it (mirrors `spawn`'s bool return).
    // `shutdown` is the self-teardown analog: the instance ends itself (and its
    // subtree). A script can't `kill` itself — you never spawned yourself, so
    // you're not in your own subtree — which is exactly why `shutdown` exists.
    // Both teardowns are deferred past the running event handler (the handler
    // is still executing in the instance's linear memory), and both close any
    // still-connected client sockets, so a script that wants a graceful exit
    // `redirect`s its players first. The entry-point instance is implicitly
    // persistent: `shutdown()` from it and `kill("")` are no-ops/rejected.
    { name: "kill", params: [{ name: "instance_id", type: T_STRING }], returnType: T_BOOL, role: "server" },
    { name: "shutdown", params: [], returnType: T_VOID, role: "server" },
    // Live player count for a script. Returns the number of connected
    // clients across every live instance running `script_label`, plus
    // every instance those instances have spawned (transitively, via the
    // spawn tree the server tracks on `spawn`). The recursion is what
    // makes this useful for sharded games: the `super_mario_bros` deployment
    // spreads players across a primary instance and the per-level worker
    // shards it spawns, so the count walks the whole
    // spawn tree to sum them. Returns 0 if no instance
    // of the label is live. See docs/instances.md §"Live player counts".
    { name: "player_count", params: [{ name: "script_label", type: T_STRING }], returnType: T_I32, role: "server" },
    // Generate a fresh unguessable identifier. Server-only because v1's
    // only use case is minting instance IDs for `spawn`; "knowledge is
    // access control" (docs/instances.md §"Addressing") means a script
    // that wants a private room must mint the ID itself rather than use
    // a guessable name. Output is a 32-char lowercase hex string (a
    // dash-stripped UUIDv4 — 122 bits of entropy).
    { name: "uuid", params: [], returnType: string32, role: "server" },
    // Inter-instance messaging — see docs/instances.md §"Inter-instance
    // communication". `tell` sends fire-and-forget bytes to the instance
    // at `url`; the runtime queues delivery and the recipient's
    // `event receive_from(from_url, data)` fires asynchronously. `my_url`
    // returns this instance's own routable URL so it can pack a return
    // address into outgoing messages. Both server-side only.
    {
        name: "tell",
        params: [
            { name: "url", type: T_STRING },
            { name: "data", type: u8Arr },
        ],
        returnType: T_VOID,
        role: "server",
    },
    { name: "my_url", params: [], returnType: T_STRING, role: "server" },
    // The routable URL of the instance that SPAWNED this one — the parent in
    // the spawn tree (docs/node-federation.md). A spawned node passes it to
    // `connect_up` to dial "whoever spawned me" without ever naming a server,
    // which is what keeps federation self-hostable (an alternative stack's
    // orchestrator resolves to itself). Empty for the entry-point instance,
    // which has no parent. Same-server resolution; a cross-server origin (a
    // central instance that sharded you to a local node) is threaded through
    // the spawn/redirect handoff instead.
    { name: "parent_url", params: [], returnType: T_STRING, role: "server" },
    // The deployment label this instance was mounted/spawned as — the id in
    // `deployments` config (or the lone script's own label). Constant for the
    // instance's whole life: the config-mounted entry and the bare rig entry
    // both report the deployment id, and a spawned worker reports the label it
    // was spawned with. Lets a shared backend (one file mounted in several
    // deployments) derive its own identity — "am I the routing entry for /<id>?"
    // (my_url == instance_url(my_deployment_label)) and "what do I spawn more of?"
    // (spawn(my_deployment_label, …)) — instead of hardcoding one deployment's id.
    { name: "my_deployment_label", params: [], returnType: T_STRING, role: "server" },
    // Build the routable URL for a same-server instance ID. Returns
    // `${server_prefix}/${id}` for non-empty IDs and the bare server
    // prefix for the empty ID (the entry-point instance). Useful for
    // scripts that just spawned a child or got an ID back from
    // `find_instance` and need the URL form for `tell`.
    { name: "instance_url", params: [{ name: "id", type: T_STRING }], returnType: T_STRING, role: "server" },
    // Convert the first `len` bytes of `buf` into a Munos string. Useful
    // when bytes arrived via `event receive` / `event receive_from` /
    // spawn handoff and the script needs to feed them to a string-typed
    // builtin like `tell`. Available to both roles since the wire
    // formats it parses can travel in either direction.
    {
        name: "string_from_bytes",
        params: [
            { name: "buf", type: u8Arr },
            { name: "len", type: T_I32 },
        ],
        returnType: T_STRING,
    },
    // Slice a string into a fresh one holding the bytes in the half-open
    // range [start, end). Byte-indexed to match `s[i]` and `len(s)` (a Munos
    // string is a byte sequence, not a grapheme sequence — the whole string
    // surface is byte-oriented). Both bounds are clamped to [0, len(s)] and
    // an empty result is returned whenever end <= start, so out-of-range or
    // inverted indices never trap — they yield "". Concatenation already
    // exists as the `+` operator (string + string / string + int), so this
    // is the missing half of the pair. Available to both roles.
    {
        name: "substring",
        params: [
            { name: "s", type: T_STRING },
            { name: "start", type: T_I32 },
            { name: "end", type: T_I32 },
        ],
        returnType: T_STRING,
    },
    { name: "slot", params: [{ name: "c", type: T_CLIENT }], returnType: T_I32, role: "server" },
    { name: "max_clients", params: [], returnType: T_I32 },
    { name: "tick_rate", params: [], returnType: T_I32, role: "server" },
    { name: "set_tick_rate", params: [{ name: "hz", type: T_I32 }], returnType: T_VOID, role: "server" },
    // Local ROM hash — client-only, returns a lowercase hex SHA-1 of the
    // ROM bytes the harness loaded. Engine-level: the script has no
    // other way to identify the cartridge (CPU reads via `read_*` see
    // mapped CPU address space, not the ROM file). What this hash maps
    // to in game-name terms is a per-script convention — a script may
    // keep its own hash→label table, or refuse to run on an unexpected
    // ROM, or ignore the hash entirely. Empty string means the harness
    // didn't supply one (e.g. headless test runs without a ROM).
    { name: "local_rom_hash", params: [], returnType: string40, role: "client" },
    // Visible viewport of the client's console in pixels (NES 256x240, Game
    // Boy 160x144, …) — lets a UI script adapt to the actual screen instead
    // of hard-coding one console's dimensions.
    { name: "screen_width", params: [], returnType: T_I32, role: "client" },
    { name: "screen_height", params: [], returnType: T_I32, role: "client" },
    // ROM control — both client-only. `pause_rom()` halts the emulator's
    // CPU; the script's `event frame` continues to fire so it can drive
    // an overlay UI (a lobby, a pause menu, a HUD with a modal — anything
    // the script wants on top of a frozen ROM) while the ROM is frozen.
    // `resume_rom()` undoes the halt. See docs/language.md "ROM control".
    { name: "pause_rom", params: [], returnType: T_VOID, role: "client" },
    { name: "resume_rom", params: [], returnType: T_VOID, role: "client" },
    // Battery-save control — client-only. The cartridge's battery-backed save
    // file (Zelda-style saves) is persisted locally by the host by default,
    // keyed to the ROM + this script bundle. A mod that owns the save itself
    // (e.g. loading/storing it over the cloud via the db_* store) calls
    // `battery_persist(false)` to turn the host's automatic write-back OFF and
    // take ownership; `battery_persist(true)` turns it back on. It is a
    // shared-game latch — one save file for the one game — so the last tenant to
    // set it wins. No-op on a non-battery cartridge. See
    // docs/battery-save-persistence.md.
    { name: "battery_persist", params: [{ name: "enabled", type: T_BOOL }], returnType: T_VOID, role: "client" },
    // input — client-side gamepad polling. The complementary `event input`
    // covers edge-triggered handling; `input_held` / `input_pressed` are
    // sampled against the frame-start input snapshot so multiple calls
    // within one `event frame` see a consistent view (see docs/language.md
    // "Input"). `consume_input` is only valid inside an `event input`
    // handler — the host runtime traps if it's called elsewhere, but the
    // type-checker treats it as any other client-side builtin.
    { name: "input_held", params: [{ name: "button", type: T_I32 }], returnType: T_BOOL, role: "client" },
    { name: "input_pressed", params: [{ name: "button", type: T_I32 }], returnType: T_BOOL, role: "client" },
    { name: "consume_input", params: [], returnType: T_VOID, role: "client" },
    // hopping
    {
        name: "hop",
        params: [{ name: "url", type: T_STRING }, { name: "handoff", type: u8Arr }],
        returnType: T_VOID,
        role: "client",
    },
    // Self-unload: close the WebSocket, stop the frame loop, and tear
    // down the script. After this call the runtime is fully offline —
    // no further `event_*` handlers fire, no socket activity, no per-
    // frame work. The underlying ROM keeps running on whatever
    // emulator host wraps the script. Used to drop into a "pure local
    // play" mode from a lobby. The script is responsible for any state
    // cleanup it wants visible (e.g. resume_rom() to lift a pause)
    // before calling — once disconnect() runs, the script no longer
    // has a chance to do anything.
    {
        name: "disconnect",
        params: [],
        returnType: T_VOID,
        role: "client",
    },
    // Multi-script client (docs/multi-script-client.md). `spawn_local` runs
    // ANOTHER script at `url` as a child mod over the SAME game, side by side
    // with this one (the explicit-orchestrator door: a lobby-style script
    // stacking mods). It returns a handle immediately; the source fetch is
    // async, and the handle is valid for `despawn` even while loading.
    // `despawn` tears down a child the caller spawned (and that child's own
    // subtree). A script can only reach its OWN subtree — never a sibling —
    // so mods can't eject each other. Both no-op on hosts that don't
    // implement multi-script (the warn-once stub).
    //
    // When `url` names a DEPLOYMENT (a member array — e.g. a gameplay bundle),
    // the host mounts every member locally over the shared local bus, exactly
    // as a group hop resolves the served array (docs/level-player-sessions.md
    // §6). `handoff` is the per-PLAYER connect payload (→ each member's
    // `event connect`); the optional `init_data` is the per-INSTANCE spawn seed
    // (→ each member's `event init`), the local-mount mirror of the server's
    // `spawn(..., data, ...)`. A private Level Player launch spawn_locals the
    // gameplay deployment with the chosen level's blob as `init_data`, so its
    // store is born holding the level (§4). Omit `init_data` for a plain
    // single-mod spawn with no seed.
    {
        name: "spawn_local",
        params: [
            { name: "url", type: T_STRING },
            { name: "handoff", type: u8Arr },
            { name: "init_data", type: u8Arr, optional: true },
        ],
        returnType: T_U32,
        role: "client",
    },
    {
        name: "despawn",
        params: [{ name: "handle", type: T_U32 }],
        returnType: T_VOID,
        role: "client",
    },
    {
        name: "redirect",
        params: [
            { name: "client", type: T_CLIENT },
            { name: "url", type: T_STRING },
            { name: "handoff", type: u8Arr },
        ],
        returnType: T_VOID,
        role: "server",
    },
    // "Redirect to LOCAL, keep me as parent" (docs/node-federation.md §Stage C).
    // Tells one client to spawn its OWN local server for the current deployment
    // and keep THIS instance as that local server's uplink parent — the
    // heavy/private hosting moves to the player's machine while this instance
    // stays the thin upload sink. The parent URL is stamped by the runtime
    // (my_url()), so the script names no address. Counterpart of `redirect`,
    // which instead hops the client to another REMOTE instance.
    {
        name: "host_locally",
        params: [
            { name: "client", type: T_CLIENT },
            { name: "handoff", type: u8Arr },
        ],
        returnType: T_VOID,
        role: "server",
    },
]);
export function findBuiltin(name) {
    return BUILTIN_FUNCTIONS.find(f => f.name === name) ?? null;
}
// ----- cast functions -----
// Treated separately because `i32(x)` etc. is a syntactic call but has
// different semantics: a single positional argument of any integer type.
export const CAST_NAMES = new Set([
    "i8", "i16", "i32", "u8", "u16", "u32",
]);
// ----- events -----
export const BUILTIN_EVENTS = Object.freeze([
    // client
    //
    // `event frame` on a client fires at OAM DMA time on emulator-
    // backed clients (the moment $4014 commits sprite state). RAM
    // reads inside reflect that instant. The previous `event render`
    // event has been collapsed into this one — see
    // docs/fidelity-rig-agent-guide.md.
    {
        name: "frame", role: "client",
        params: [{ name: "frame_num", type: T_I32 }],
    },
    {
        // The optional leading `channel: i32` overload lets a client member that
        // `listen`s on more than one channel tell which one a frame arrived on;
        // the host passes the channel it already routed by. Without it, behaviour
        // is exactly as before (bare body). See docs/multi-script-client.md.
        name: "receive", role: "client",
        params: [{ name: "data", type: u8Arr }],
        overloads: [
            [{ name: "channel", type: T_I32 }, { name: "data", type: u8Arr }],
        ],
    },
    {
        name: "input", role: "client",
        params: [{ name: "button", type: T_I32 }, { name: "pressed", type: T_BOOL }],
    },
    {
        // Interest-management leave signal — see docs/interest-management.md.
        // Fires on the RECEIVING client when a peer leaves this client's interest
        // set: it scrolled out of overlap (AoI cull), disconnected, or was timed
        // out / kicked. The host owns the edge and delivers this exactly once per
        // relevant→irrelevant transition, reliably, so the script drops the ghost
        // cleanly instead of dead-reckoning a phantom at the screen edge. On
        // (re)entry the host simply resumes delivery and the next `event receive`
        // re-establishes the ghost. This unifies three things that are the same
        // event (AoI exit / disconnect / timeout) and is the client-facing half of
        // the lifecycle-control "tell clients a player is gone" need.
        name: "peer_left", role: "client",
        params: [{ name: "slot", type: T_I32 }],
    },
    // server
    {
        // Instance startup hook. Fires exactly once, when the instance is
        // created — after the module's top-level initializers run, and before
        // any client connects or any `tick` fires. `data` is the payload the
        // parent passed to `spawn(label, id, data, persistent)`; it is empty
        // for the entry-point instance (created by the server itself, not by a
        // script's `spawn`). This is the home for per-instance configuration —
        // e.g. an orchestrator handing a freshly-spawned worker its assignment,
        // a seed, or a ruleset — and is deliberately distinct from
        // `event connect`'s per-player handoff. See docs/instances.md
        // §"The spawn primitive".
        name: "init", role: "server",
        params: [{ name: "data", type: u8Arr }],
    },
    {
        name: "tick", role: "server",
        params: [{ name: "tick_num", type: T_I32 }],
    },
    {
        // The optional `channel: i32` overload (placed right after `from`) lets a
        // server member that `listen`s on more than one channel tell which one a
        // frame arrived on; the host passes the channel it already routed by.
        // Without it, behaviour is exactly as before. See docs/multi-script-client.md.
        name: "receive", role: "server",
        params: [{ name: "from", type: T_CLIENT }, { name: "data", type: u8Arr }],
        overloads: [
            [{ name: "from", type: T_CLIENT }, { name: "channel", type: T_I32 }, { name: "data", type: u8Arr }],
        ],
    },
    {
        name: "connect", role: "server",
        params: [{ name: "client", type: T_CLIENT }, { name: "handoff", type: u8Arr }],
    },
    {
        name: "disconnect", role: "server",
        params: [{ name: "client", type: T_CLIENT }],
    },
    {
        // Inter-instance message arrival. `from_url` is the sender's
        // `my_url()`; the recipient can pack it into outgoing replies to
        // talk back. See docs/instances.md §"Inter-instance communication".
        name: "receive_from", role: "server",
        params: [{ name: "from_url", type: T_STRING }, { name: "data", type: u8Arr }],
    },
    {
        // Arrival from this instance's PARENT over the link opened by
        // `connect_up` — the "from above" counterpart of the client's
        // `event receive(data)`. There is exactly one parent, so like the
        // client receive it carries no sender handle. See
        // docs/node-federation.md.
        name: "receive_from_above", role: "server",
        params: [{ name: "data", type: u8Arr }],
    },
    // Persistence diagnostic event — fires in addition to a db_* callback
    // when the underlying operation hit infrastructure trouble (DB
    // unreachable, quota overflow, etc.). Declaring it is optional; if
    // the script does not declare it, infrastructure failures are still
    // visible through the success=false callback path. See
    // docs/persistence.md §The error event.
    {
        name: "db_error", role: "client",
        params: [
            { name: "op", type: T_STRING },
            { name: "namespace", type: T_STRING },
            { name: "key", type: T_STRING },
            { name: "msg", type: T_STRING },
        ],
    },
    {
        name: "db_error", role: "server",
        params: [
            { name: "op", type: T_STRING },
            { name: "namespace", type: T_STRING },
            { name: "key", type: T_STRING },
            { name: "msg", type: T_STRING },
        ],
    },
    // Fetch diagnostic event — fires in addition to a fetch callback when
    // the operation did not deliver bytes (network error, non-2xx,
    // scheme rejection, rate-limit overflow, CORS denial). Optional;
    // failures remain observable via the success=false callback path
    // when the script does not declare it. Client-only — fetch itself is
    // client-only in v1. See docs/fetch.md §The error event.
    {
        name: "fetch_error", role: "client",
        params: [
            { name: "url", type: T_STRING },
            { name: "msg", type: T_STRING },
        ],
    },
]);
export function findEvent(name, role) {
    return BUILTIN_EVENTS.find(e => e.name === name && e.role === role) ?? null;
}
// The distinct roles an event of this name can be declared for. Used to
// route events declared in the common (outside-block) section: a name
// with exactly one role auto-routes there; a name with more than one
// (`receive`, `db_error`) is ambiguous in common and must live in a role
// block; a name with none is unknown.
export function eventRolesForName(name) {
    const roles = [];
    for (const e of BUILTIN_EVENTS) {
        if (e.name === name && !roles.includes(e.role))
            roles.push(e.role);
    }
    return roles;
}
//# sourceMappingURL=builtins.js.map